// ============================================================================
// SYNC FAILURE SERVICE - Tracks and manages failed platform sync attempts
// ============================================================================

import { supabase } from "@/integrations/supabase/client";

export interface SyncFailure {
  id: string;
  contentType: string;
  contentId: string;
  queueId: string | null;
  errorMessage: string;
  attempts: number;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  contentTitle?: string;
  showId?: string;
  showTitle?: string;
  podcasterName?: string;
  // Latest per-platform error from episode_platform_ids / show_platform_ids,
  // surfaced so admins see the actionable cause when the stored error_message
  // is just a transport-layer string.
  latestPlatformError?: { platform: string; message: string };
}

function mapRow(row: any): SyncFailure {
  return {
    id: row.id,
    contentType: row.content_type,
    contentId: row.content_id,
    queueId: row.queue_id,
    errorMessage: row.error_message,
    attempts: row.attempts,
    resolved: row.resolved,
    resolvedAt: row.resolved_at,
    resolvedBy: row.resolved_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Log a sync failure to the database
 */
export async function logSyncFailure(params: {
  contentType: string;
  contentId: string;
  queueId: string | null;
  errorMessage: string;
  attempts: number;
}): Promise<void> {
  const { error } = await supabase
    .from("sync_failures")
    .insert({
      content_type: params.contentType,
      content_id: params.contentId,
      queue_id: params.queueId,
      error_message: params.errorMessage,
      attempts: params.attempts,
    });

  if (error) {
    console.error("Failed to log sync failure:", error);
  }
}

/**
 * Defensive parse of a platform-side error_message. Some platforms (e.g.
 * Simplecast) store a JSON blob like `{success:false,error:"…",timestamp:"…"}`;
 * others store a plain string. Always returns a human-readable string.
 */
export function extractPlatformErrorText(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed?.error === "string" && parsed.error.trim()) return parsed.error.trim();
      if (typeof parsed?.message === "string" && parsed.message.trim()) return parsed.message.trim();
    } catch {
      // fall through and return the raw string
    }
  }
  return trimmed;
}

/**
 * Get all sync failures (admin only), enriched with content titles
 */
export async function getSyncFailures(
  filter: "all" | "unresolved" | "resolved" = "unresolved"
): Promise<SyncFailure[]> {
  let query = supabase
    .from("sync_failures")
    .select("*")
    .order("created_at", { ascending: false });

  if (filter === "unresolved") {
    query = query.eq("resolved", false);
  } else if (filter === "resolved") {
    query = query.eq("resolved", true);
  }

  const { data, error } = await query;
  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Batch lookup content titles
  const showIds = data.filter((r) => r.content_type === "show").map((r) => r.content_id);
  const episodeIds = data.filter((r) => r.content_type === "episode").map((r) => r.content_id);

  const [showsResult, episodesResult] = await Promise.all([
    showIds.length > 0
      ? supabase.from("shows").select("id, title, user_id").in("id", showIds)
      : Promise.resolve({ data: [] as any[] }),
    episodeIds.length > 0
      ? supabase.from("episodes").select("id, title, user_id, show_id").in("id", episodeIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);

  const showsMap = new Map((showsResult.data || []).map((s: any) => [s.id, s]));
  const episodesMap = new Map((episodesResult.data || []).map((e: any) => [e.id, e]));

  // Fetch show titles for episodes that have a show_id
  const episodeShowIds = (episodesResult.data || [])
    .map((e: any) => e.show_id)
    .filter((id: string | null): id is string => !!id && !showsMap.has(id));
  const uniqueEpisodeShowIds = [...new Set(episodeShowIds)];

  if (uniqueEpisodeShowIds.length > 0) {
    const { data: extraShows } = await supabase
      .from("shows")
      .select("id, title")
      .in("id", uniqueEpisodeShowIds);
    (extraShows || []).forEach((s: any) => showsMap.set(s.id, s));
  }

  // Collect user IDs for podcaster names
  const userIds = new Set<string>();
  (showsResult.data || []).forEach((s: any) => userIds.add(s.user_id));
  (episodesResult.data || []).forEach((e: any) => userIds.add(e.user_id));

  const profilesResult = userIds.size > 0
    ? await supabase.from("profiles").select("id, display_name").in("id", [...userIds])
    : { data: [] as any[] };

  const profilesMap = new Map((profilesResult.data || []).map((p: any) => [p.id, p.display_name]));

  // Enrich with the latest per-platform error so admins see the actionable
  // cause when the stored error_message is a transport-layer string.
  const sb: any = supabase;
  const [episodePlatformResult, showPlatformResult]: [{ data: any[] | null }, { data: any[] | null }] = await Promise.all([
    episodeIds.length > 0
      ? sb
          .from("episode_platform_ids")
          .select("episode_id, platform, status, error_message, updated_at")
          .in("episode_id", episodeIds)
          .eq("status", "failed")
          .order("updated_at", { ascending: false })
      : Promise.resolve({ data: [] }),
    showIds.length > 0
      ? sb
          .from("show_platform_ids")
          .select("show_id, platform, status, error_message, updated_at")
          .in("show_id", showIds)
          .eq("status", "failed")
          .order("updated_at", { ascending: false })
      : Promise.resolve({ data: [] }),
  ]);

  // First failed row per content id wins (already ordered desc).
  const episodePlatformMap = new Map<string, { platform: string; message: string }>();
  for (const row of (episodePlatformResult.data || []) as any[]) {
    if (episodePlatformMap.has(row.episode_id)) continue;
    const message = extractPlatformErrorText(row.error_message);
    if (message) episodePlatformMap.set(row.episode_id, { platform: row.platform, message });
  }
  const showPlatformMap = new Map<string, { platform: string; message: string }>();
  for (const row of (showPlatformResult.data || []) as any[]) {
    if (showPlatformMap.has(row.show_id)) continue;
    const message = extractPlatformErrorText(row.error_message);
    if (message) showPlatformMap.set(row.show_id, { platform: row.platform, message });
  }

  return data.map((row) => {
    const item = mapRow(row);
    const content = row.content_type === "show"
      ? showsMap.get(row.content_id)
      : episodesMap.get(row.content_id);
    item.contentTitle = content?.title || "Unknown";
    item.podcasterName = content?.user_id ? profilesMap.get(content.user_id) || "Unknown" : "Unknown";
    if (row.content_type === "episode" && content?.show_id) {
      item.showId = content.show_id;
      item.showTitle = showsMap.get(content.show_id)?.title || undefined;
    } else if (row.content_type === "show") {
      item.showId = row.content_id;
    }
    const platformErr = row.content_type === "episode"
      ? episodePlatformMap.get(row.content_id)
      : showPlatformMap.get(row.content_id);
    if (platformErr) item.latestPlatformError = platformErr;
    return item;
  });
}

/**
 * Mark a sync failure as resolved
 */
export async function resolveSyncFailure(failureId: string): Promise<void> {
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("sync_failures")
    .update({
      resolved: true,
      resolved_at: new Date().toISOString(),
      resolved_by: session.session.user.id,
    })
    .eq("id", failureId);

  if (error) throw error;
}

/**
 * Delete a sync failure record
 */
export async function deleteSyncFailure(failureId: string): Promise<void> {
  const { error } = await supabase
    .from("sync_failures")
    .delete()
    .eq("id", failureId);

  if (error) throw error;
}

/**
 * Get count of unresolved sync failures (for sidebar badge)
 */
export async function getUnresolvedCount(): Promise<number> {
  const { count, error } = await supabase
    .from("sync_failures")
    .select("*", { count: "exact", head: true })
    .eq("resolved", false);

  if (error) return 0;
  return count || 0;
}

/**
 * Fetch the most recent failed platform-side error for a single piece of content.
 * Reuses the same parser as the bulk Failed Syncs query so a single source of truth
 * exists for "what platform rejected this and why".
 */
export async function getLatestPlatformError(
  contentType: "show" | "episode",
  contentId: string,
): Promise<{ platform: string; message: string } | null> {
  const sb: any = supabase;
  const table = contentType === "episode" ? "episode_platform_ids" : "show_platform_ids";
  const idCol = contentType === "episode" ? "episode_id" : "show_id";
  const { data, error } = await sb
    .from(table)
    .select(`platform, error_message, updated_at, ${idCol}`)
    .eq(idCol, contentId)
    .eq("status", "failed")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  const message = extractPlatformErrorText(data.error_message);
  if (!message) return null;
  return { platform: data.platform, message };
}
